import React from 'react';

/**
 * Read-only, compact Babylon stock card surfaced inline in the public /coach
 * chat when a ticker is tapped. Mirrors the coach / real-yield aesthetic
 * (dark ledger, mono numerals, blue spine). Presentation only — no actions,
 * no CTA (a later phase). All fields are nullable (API-only data policy) and
 * render "—" when absent.
 */

export interface PublicStockCardData {
  symbol: string;
  company_name: string | null;
  sector: string | null;
  current_price: number | null;
  change_percent: number | null;
  cc_score: number | null; // 0–100
  quality_score: number | null; // 0–50 (Dimension 1)
  timing_score: number | null; // 0–50 (Dimension 2)
  iv_rank: number | null;
  best_ann_yield: number | null;
  dividend_yield: number | null;
  next_earnings_date: string | null; // ISO date or null
  market_cap: string | null; // pre-formatted, e.g. "$123.4B"
}

const DASH = '—';

const fmtMoney = (v: number | null): string =>
  v == null ? DASH : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtPct = (v: number | null, sign = false): string => {
  if (v == null) return DASH;
  const s = `${Math.abs(v) < 100 ? v.toFixed(1) : v.toFixed(0)}%`;
  return sign && v >= 0 ? `+${s}` : s;
};

/** Days from today to an ISO date string, or null if unparseable. */
const daysUntil = (iso: string | null): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.round((t - Date.now()) / 86400000);
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return DASH;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return DASH;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** A labelled proportional sub-score bar (Quality / Timing, each out of 50). */
const ScoreBar: React.FC<{ label: string; value: number | null; max: number }> = ({
  label,
  value,
  max,
}) => {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="psc-bar">
      <div className="psc-bar-head">
        <span className="psc-bar-label">{label}</span>
        <span className="psc-bar-val">{value == null ? DASH : `${value}/${max}`}</span>
      </div>
      <div className="psc-bar-track">
        <div className="psc-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="psc-stat">
    <span className="psc-stat-label">{label}</span>
    <span className="psc-stat-val">{value}</span>
  </div>
);

const PublicStockCard: React.FC<{ data: PublicStockCardData }> = ({ data }) => {
  const change = data.change_percent;
  const changeClass = change == null ? '' : change >= 0 ? ' psc-up' : ' psc-down';

  const earningsDays = daysUntil(data.next_earnings_date);
  const earningsSoon = earningsDays != null && earningsDays >= 0 && earningsDays <= 45;

  return (
    <div className="psc-card" role="group" aria-label={`${data.symbol} stock card`}>
      {/* Header */}
      <div className="psc-head">
        <div className="psc-id">
          <span className="psc-symbol">{data.symbol}</span>
          {data.company_name && <span className="psc-name">{data.company_name}</span>}
        </div>
        {data.sector && <span className="psc-sector">{data.sector}</span>}
      </div>

      {/* Price + change */}
      <div className="psc-price-row">
        <span className="psc-price">{fmtMoney(data.current_price)}</span>
        {change != null && (
          <span className={`psc-change${changeClass}`}>{fmtPct(change, true)}</span>
        )}
      </div>

      {/* CC Score headline + sub-score bars */}
      <div className="psc-score">
        <div className="psc-score-head">
          <span className="psc-score-label">CC Score</span>
          <span className="psc-score-val">
            {data.cc_score == null ? DASH : data.cc_score}
            <span className="psc-score-max">/100</span>
          </span>
        </div>
        <div className="psc-bars">
          <ScoreBar label="Quality" value={data.quality_score} max={50} />
          <ScoreBar label="Timing" value={data.timing_score} max={50} />
        </div>
      </div>

      {/* Secondary stats */}
      <div className="psc-stats">
        <Stat label="IV Rank" value={fmtPct(data.iv_rank)} />
        <Stat label="Best ann. yield" value={fmtPct(data.best_ann_yield)} />
        <Stat label="Dividend" value={fmtPct(data.dividend_yield)} />
        <Stat
          label="Next earnings"
          value={
            <>
              {fmtDate(data.next_earnings_date)}
              {earningsSoon && (
                <span className="psc-earnings-flag" title="Earnings within ~45 days">
                  ⚑
                </span>
              )}
            </>
          }
        />
      </div>

      <div className="psc-foot">via Babylon's screener</div>
    </div>
  );
};

/** Slim skeleton shown while a card is fetching. */
export const PublicStockCardSkeleton: React.FC = () => (
  <div className="psc-card psc-card--skel" aria-hidden="true">
    <div className="psc-skel-line psc-skel-line--head" />
    <div className="psc-skel-line psc-skel-line--price" />
    <div className="psc-skel-line psc-skel-line--bar" />
    <div className="psc-skel-line psc-skel-line--bar" />
  </div>
);

/** Quiet inline notice when a card can't load (non-404 error). */
export const PublicStockCardError: React.FC = () => (
  <div className="psc-card psc-card--error">Couldn't load that card right now.</div>
);

export default PublicStockCard;
